import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Marquee from "../components/Marquee";
import FeaturedDesigns from "../components/FeaturedDesigns";
import StreakWidget from "../components/StreakWidget";
import StreakTeaser from "../components/StreakTeaser";
import { ArrowRight, Crown, MessageCircleQuestion, ShoppingBag, Video, Beer, Flame } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/31825286/pexels-photo-31825286.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const LIFESTYLE_IMG = "https://images.unsplash.com/photo-1537290946-0a1420e210dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxjb3VudHJ5JTIwbGlmZXN0eWxlJTIwYmVlciUyMHBpY2t1cCUyMHRydWNrfGVufDB8fHx8MTc3ODQ3MDkwMnww&ixlib=rb-4.1.0&q=85";

export default function Home({ onAuthClick }) {
  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section className="relative min-h-[90vh] grain overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E0B] via-[#0A0E0B]/85 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E0B] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-20 md:pt-32 pb-16">
          <div className="inline-block px-4 py-2 bg-[#DD5422] text-[#0A0E0B] font-anton uppercase tracking-widest text-xs mb-6 fade-up">
            ★ Born In The Back Roads
          </div>
          <h1 className="font-anton text-6xl md:text-[9rem] leading-[0.85] uppercase tracking-tighter fade-up">
            Everythang<br />
            <span className="text-[#DD5422]">Redneck</span>
          </h1>
          <p className="mt-8 max-w-xl text-lg md:text-xl text-[#8D9688] leading-relaxed fade-up" style={{ animationDelay: "0.15s" }}>
            Cold beer. Hot girls. Big trucks. Mud, guns, and country roads. Threads and merch for folks who live loud, ride hard, and ain&apos;t apologizin&apos;.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 fade-up" style={{ animationDelay: "0.3s" }}>
            <Link to="/shop">
              <Button data-testid="hero-shop-btn" className="bg-[#DD5422] text-[#0A0E0B] hover:bg-[#FF6B35] rounded-none font-anton uppercase tracking-widest text-base h-14 px-8 border-2 border-[#DD5422]">
                Shop the Merch <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/ask">
              <Button data-testid="hero-ask-btn" variant="outline"
                className="bg-transparent border-2 border-[#2C332A] hover:border-[#DD5422] hover:bg-transparent text-[#E5E5E5] rounded-none font-anton uppercase tracking-widest text-base h-14 px-8">
                Ask a Redneck
              </Button>
            </Link>
          </div>

          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 fade-up" style={{ animationDelay: "0.45s" }}>
            {[
              { num: "18+", label: "Drops Live" },
              { num: "24/7", label: "Cousin Cletus" },
              { num: "100%", label: "American" },
              { num: "0", label: "Apologies" },
            ].map((s) => (
              <div key={s.label} className="border-2 border-[#2C332A] p-4 bg-[#151A14]/50">
                <div className="font-anton text-4xl md:text-5xl text-[#DD5422]">{s.num}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#8D9688] mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* === LATEST DROP: REDNECK REVOLUTION ALBUM === */}
      <section className="relative overflow-hidden border-y-2 border-[#DD5422]" data-testid="album-hero">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E0B] via-[#0A0E0B]/85 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <Link to="/music" className="block group" data-testid="album-hero-cover">
              <img
                src="/api/designs/album-cover-everythang-redneck.png"
                alt="Redneck Revolution album cover"
                className="w-full max-w-sm mx-auto border-4 border-[#DD5422] shadow-2xl group-hover:scale-105 transition-transform duration-500"
              />
            </Link>
            <div>
              <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-xs mb-3 flex items-center gap-2">
                <Flame className="w-4 h-4" fill="#DD5422" /> / Just Dropped
              </div>
              <h2 className="font-anton text-5xl md:text-7xl uppercase tracking-tight leading-none">
                Redneck <br /><span className="text-[#DD5422]">Revolution.</span>
              </h2>
              <p className="text-[#8D9688] mt-4 max-w-xl text-lg">
                8 original tracks. Anthems of unity, brotherhood, and takin&apos; back what&apos;s ours.
                Cover shot in the artist&apos;s own front yard — that&apos;s the aurora, real as it gets.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/music" data-testid="album-hero-play">
                  <Button className="bg-[#DD5422] text-[#0A0E0B] hover:bg-[#FF6B35] rounded-none font-anton uppercase tracking-widest h-14 px-8 border-2 border-[#DD5422] text-base">
                    Press Play <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/message" data-testid="album-hero-message">
                  <Button variant="outline" className="border-2 border-[#DD5422] text-[#DD5422] hover:bg-[#DD5422] hover:text-[#0A0E0B] rounded-none font-anton uppercase tracking-widest h-14 px-8 text-base bg-transparent">
                    Read The Message
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <StreakWidget />
      <StreakTeaser onSignIn={() => onAuthClick?.("login")} />

      <FeaturedDesigns />

      {/* BENTO FEATURES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20" data-testid="bento-features">
        <div className="mb-12">
          <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-sm mb-2">/ What&apos;s Inside</div>
          <h2 className="font-anton text-4xl md:text-6xl uppercase tracking-tight">The Whole Damn Spread</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link to="/shop" data-testid="feature-shop" className="md:col-span-2 group relative overflow-hidden bg-[#151A14] border-2 border-[#2C332A] hover:border-[#DD5422] transition-colors min-h-[400px]">
            <img src={LIFESTYLE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E0B] via-[#0A0E0B]/60 to-transparent" />
            <div className="relative z-10 p-8 h-full flex flex-col justify-end">
              <ShoppingBag className="w-12 h-12 text-[#DD5422] mb-4" strokeWidth={1.5} />
              <h3 className="font-anton text-4xl md:text-5xl uppercase tracking-tight">The Merch Stand</h3>
              <p className="text-[#8D9688] mt-2 max-w-md">T-shirts, hoodies, hats, and drinkware loud enough for the back forty.</p>
              <div className="mt-4 inline-flex items-center gap-2 font-anton uppercase tracking-widest text-sm text-[#DD5422]">
                Shop Now <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>

          <Link to="/ask" data-testid="feature-ask" className="group relative overflow-hidden bg-[#4B5320] border-2 border-[#2C332A] hover:border-[#DD5422] transition-colors min-h-[400px]">
            <div className="p-8 h-full flex flex-col justify-between">
              <MessageCircleQuestion className="w-12 h-12 text-[#0A0E0B]" strokeWidth={1.5} />
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#0A0E0B]/70 mb-2">Powered by Cousin Cletus AI</div>
                <h3 className="font-anton text-3xl md:text-4xl uppercase tracking-tight text-[#0A0E0B]">Ask A Redneck</h3>
                <p className="text-[#0A0E0B]/80 mt-2 text-sm">Got a question? Cousin Cletus&apos;s got an answer. Reckon it&apos;ll help.</p>
              </div>
            </div>
          </Link>

          <Link to="/videos" data-testid="feature-videos" className="group relative overflow-hidden bg-[#151A14] border-2 border-[#2C332A] hover:border-[#DD5422] transition-colors min-h-[300px]">
            <div className="p-8 h-full flex flex-col justify-between">
              <Video className="w-12 h-12 text-[#DD5422]" strokeWidth={1.5} />
              <div>
                <h3 className="font-anton text-3xl md:text-4xl uppercase tracking-tight">Funny Videos</h3>
                <p className="text-[#8D9688] mt-2 text-sm">Country bangers, mud bogs, and pranks. Crank the speakers.</p>
              </div>
            </div>
          </Link>

          <Link to="/subscribe" data-testid="feature-subscribe" className="md:col-span-2 group relative overflow-hidden border-2 border-[#DD5422] hover:bg-[#DD5422] transition-colors min-h-[300px]"
            style={{ background: "linear-gradient(135deg, #151A14 0%, #2C332A 100%)" }}>
            <div className="p-8 h-full flex items-center justify-between gap-6 flex-wrap">
              <div>
                <Crown className="w-12 h-12 text-[#DD5422] group-hover:text-[#0A0E0B] mb-3" strokeWidth={1.5} />
                <h3 className="font-anton text-4xl md:text-5xl uppercase tracking-tight group-hover:text-[#0A0E0B]">VIP Redneck</h3>
                <p className="text-[#8D9688] group-hover:text-[#0A0E0B]/80 mt-2 max-w-md">Monthly subscription. Drops first, discounts always, members-only videos.</p>
              </div>
              <div className="font-anton text-6xl md:text-7xl text-[#DD5422] group-hover:text-[#0A0E0B]">$9.99<span className="text-xl">/mo</span></div>
            </div>
          </Link>
        </div>
      </section>

      {/* CALLOUT */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20" data-testid="callout-section">
        <div className="bg-[#151A14] border-2 border-[#2C332A] p-8 md:p-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Flame className="w-6 h-6 text-[#DD5422]" />
              <span className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-sm">Hot Off The Truck</span>
            </div>
            <h2 className="font-anton text-4xl md:text-6xl uppercase tracking-tight">Built American,<br /><span className="text-[#DD5422]">Worn Loud</span>.</h2>
            <p className="text-[#8D9688] mt-6 text-lg">Every drop is designed in-house, printed on heavy cotton, and built to last through bonfires, mud rides, and Saturday night shenanigans.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Beer, label: "Cold Beer Approved" },
              { icon: Flame, label: "Bonfire Tested" },
              { icon: ShoppingBag, label: "Free US Shipping $50+" },
              { icon: Crown, label: "VIP Drops First" },
            ].map((s, i) => (
              <div key={i} className="border-2 border-[#2C332A] p-4">
                <s.icon className="w-6 h-6 text-[#DD5422] mb-2" />
                <div className="font-anton uppercase tracking-wide text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
